import React, { useState, useEffect, useRef } from 'react';
import { Grid, Card, Toolbar, LinearProgress, CardContent, Table, TableHead, TableRow, TableBody, TableCell, Dialog, DialogContent, DialogActions, Typography, Button, TextField as MuiTextField} from '@mui/material';
import axios from 'axios';
import Config from '../../../config';
import { Link } from 'react-router-dom';
import ExtendedSelectBase from '../../../components/select';
import { ParseSpecification } from './product_feed_spec';
import { GetProductPartnerId, SetProductPartnerId } from './product';
import DownloadButton from '../../../components/download_button';
import { RunRule } from '../shared/validation';
import { Notifier } from '../../documents/document_components';
import { ShareDialog, DeleteDialog, SaveDialog, SaveAsDialog, loadProfiles, saveProfile } from '../../files/import_export_profiles';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import withCatalogEnabled from './with_catalog_enabled';
import Helpers from '../../../helpers';
import {isValidExpression} from '../shared/mapping_checker';
import FileSaverBase from 'filesaver.js-npm';
import DropZone from '../../files/dropzone';
import {cleanCondition, parseCondition, getAttrOptions, convertToGroup, RuleEditor, MappingEditor, RecursiveConditionField} from '../shared/mapping_templates';

// Relax untyped/forwardRef component + lib types to `any` (behavior unchanged).
const ExtendedSelect: any = ExtendedSelectBase;
const FileSaver: any = FileSaverBase;

const profileType = 'RetailerCatalog';

const cmp = function(a, b) {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
}

const getDefaultMappings = (spec: any, attributes?: any): any => {
    let mappings = [];
    let mapped = [];
    for(const attr of attributes){
        let field = spec.Fields.find(r => (r.Name || '').toLowerCase() === attr.Name
        || (r.Name || '').toLowerCase() === attr.FriendlyName
        || (r.DescriptiveName || '').toLowerCase() === attr.Name
        || (r.DescriptiveName || '').toLowerCase() === attr.FriendlyName
        );
        if(field && mapped.indexOf(field) === -1){
            mappings.push({field: attr.Name, alias: field.Name});
            mapped.push(field);
        }
    }
    return mappings;
}

// Converted from a class component to an idiomatic function component. State is a
// single object updated with a functional merging setState; handlers read the
// current values via closure, and the two cross-async reads (mount default
// mappings, save-as/share re-select) receive their fresh data as arguments -- so
// no state-mirror ref is needed. profileSelectRef/setRuleRef stay genuine refs.
function CatalogExport(props: any) {
    const [state, setStateRaw] = useState<any>({loaded: false, attributes: [], mappings: [], fieldOptions: [], missingFields: [], showExport: false,
    showError: false, showSave: false, showSaveAs: false, showShare: false, showEdit: false, confirmDelete: false,
    profile: {}, profiles: [], showFilter: false, showProfileImport: false, rules: [], showRuleEdit: false});
    // Single setter: accepts an object partial OR a functional updater
    // `(prev) => partial`. The updater form lets read-modify-write handlers read
    // the latest state (parity with the old synchronous stateRef mirror) without
    // reaching for the raw setter. Merge matches the old class this.setState.
    const setState = (update: any) => setStateRaw((prev: any) => ({ ...prev, ...(typeof update === 'function' ? update(prev) : update) }));
    const profileSelectRef = useRef<any>(null);
    const setRuleRef = useRef<any>(undefined);

    const reloadProfiles = async () => {
        let profiles = await loadProfiles(profileType);
        setState({profiles});
        return profiles;
    };

    const componentDidMount = async () => {
        let partners = [];
        if(!props.isRetailer){
            partners = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        }
        let storedId = GetProductPartnerId();
        if(partners.length > 0){
            if(!storedId || !partners.find(r => r.Id.toString() === storedId.toString())){
                storedId = partners[0].Id;
                SetProductPartnerId(storedId);
            }
        }
        let attributes = (await axios.get(Config.api + '/api/v1/product/catalog/attributes')).data.Records;
        attributes.push({Name: 'last_update', FriendlyName: "Last Update", Type: "text"});
        let attrOptions = attributes.map(r => ({label: r.Name, value: r.Name}));
        setState({partners, partner: partners.length > 0 ? storedId : '', attributes, attrOptions, originalAttributes: attrOptions});
        await reloadProfiles();
        loadData(storedId, attributes);
    };

    useEffect(() => {
        componentDidMount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkMappings = (spec, mappings) => {
        if(!spec){
            return [];
        }
        let requiredFields = [];
        let testObj = {};
        let keys = mappings.map(r => r.alias);
        keys = keys.filter((r, i) => keys.indexOf(r) === i);
        for(let i = 0; i < keys.length; i++){
             testObj[keys[i]] = 'value';
        }
        spec.Fields.filter(r => r.Rules).forEach(field => {
            if(!field.Rules.filter(r => r.Type === 'required').every(rule => RunRule(testObj, rule).IsValid)){
                requiredFields.push(field);
            }
        });
        return requiredFields.map(r => r.DescriptiveName);
    };

    const getProfile = () => {
        let profile = {...state.profile};
        profile.Fields = [...state.mappings];
        if(state.filter){
            profile.Filter = {...state.filter};
        }
        if(state.rules && state.rules.length > 0){
            profile.Rules = [...state.rules];
        }
        return profile;
    };

    const computeDefaultMappings = (spec: any, attributes: any = state.attributes) => {
        return getDefaultMappings(spec, attributes);
    };

    const loadData = async (partner, attributes: any = state.attributes) => {
        if(!partner){
            return;
        }
        try{
            let spec = (await axios.get(Config.api + `/odata/company/Functions.ProductConfiguration?partnerId=${partner}`)).data;
            ParseSpecification(spec);
            // Functional updater: read mappings from the latest state so a mapping
            // the user edits while this config request is in flight is not clobbered
            // (parity with the old post-await stateRef.current.mappings read).
            setState((prev: any) => {
                let mappings = prev.mappings;
                if(mappings.length === 0 && attributes.length > 0){
                    mappings = computeDefaultMappings(spec, attributes);
                }
                let missingFields = checkMappings(spec, mappings);
                return {spec: spec, loaded: true, fieldOptions: spec.Fields.map(r => ({label: r.DescriptiveName, value: r.Name})), missingFields, mappings};
            });
        }catch(e){
            setState({loaded: true, fieldOptions: [], spec: null, missingFields: []});
            return;
        }
    };

    const loadDefaultMappings = () => {
        let mappings = computeDefaultMappings(state.spec);
        let missingFields = checkMappings(state.spec, mappings);
        setState({missingFields, mappings});
    };

    const onPartnerSwitch = async (e) => {
        if(!e){
            return;
        }
        SetProductPartnerId(e);
        setState({partner: e, spec: null, loaded: false});
        loadData(e);
    };

    const sendCatalog = async () => {
        try{
            let request = {
                RetailerId: state.partner,
                Columns: state.mappings.map(r => ({SourceField: r.field, TargetField: r.alias, Condition: r.Condition})),
                Rules: state.rules.map(r => ({SourceField: r.SourceField, TargetField: r.TargetField, Condition: r.Condition})),
                Filter: state.filter
            };
            await (axios.post(Config.api + '/api/v1/product/catalog/exporttoretailer', request));
            setState({showExport: true, exportMessage: 'Your catalog export request was received. The process may take several minutes to complete. The exported catalog will be available on the product feeds page.'});
        }catch(e){
            let error = 'Failed to export catalog.';
            if(e.response && e.response.data){
                var data = e.response.data;
                try{
                    data = JSON.parse(await new Helpers().readBlob(data));
                }catch(e){
                    // do nothing
                }
                error += " " + new Helpers().getApiErrors(data).join("\n");
            }
            setState({showError: true, error: error});
        }
    };

    const onDelete = async () => {
        await reloadProfiles();
        profileSelectRef.current.setValue('');
        setState((prev: any) => {
            let mappings = computeDefaultMappings(prev.spec, prev.attributes);
            let missingFields = checkMappings(prev.spec, mappings);
            return {profile: { Name: '', Fields: []}, confirmDelete: false, mappings, missingFields};
        });
    };

    const onSave = async () => {
        await reloadProfiles();
        setState({showSave: false});
    };

    const onSaveAs = async (newName) => {
        let profiles = await reloadProfiles();
        profileSelectRef.current.setValue(newName);
        setState({showSaveAs: false});
        onProfileSelect(newName, profiles);
    };

    const showSave = async () => {
        let values = getProfile();
        if(!values.Id){
            await saveProfile(values, profileType);
            await onSave();
        } else {
            setState({showSave: true});
        }
    };

    const showSaveAs = () => {
        setState({showSaveAs: true});
    };
    const onProfileSelect = (name, profiles: any = state.profiles) => {
        if(!name){
            return;
        }
        let selectedProfile = profiles.find(r => r.Name === name);
        let profile = {...selectedProfile};
        let mappings = profile.Fields.map(r => ({...r}));
        let rules = (profile.Rules || []).map(r => ({...r}));
        let missingFields = checkMappings(state.spec, mappings);
        let filter = profile.Filter ? {...profile.Filter} : null;
        let attrOptions = getAttrOptions(rules, state.originalAttributes);
        setState({missingFields, mappings, profile, filter, rules, attrOptions});
    };
    const shared = async (name) => {
        let profiles = await reloadProfiles();
        setState({showShare: false});
        onProfileSelect(name, profiles);
    };
    const setMapping = (m) => {
        m.Condition = cleanCondition(m.Condition);
        setState((prev: any) => {
            let mappings = [...prev.mappings];
            if(!prev.mappingIndex && prev.mappingIndex !== 0){
                mappings.push(m);
            } else {
                mappings[prev.mappingIndex] = m;
            }
            let missingFields = checkMappings(prev.spec, mappings);
            return {mappings, showEdit: false, missingFields};
        });
    };
    const setFilter = (m) => {
        m.Condition = cleanCondition(m.Condition);
        setState({filter: m.Condition, showFilter: false});
    };
    const removeMapping = (i) => {
        setState((prev: any) => {
            let mappings = [...prev.mappings];
            mappings.splice(i, 1);
            let missingFields = checkMappings(prev.spec, mappings);
            return {mappings, missingFields};
        });
    };
    const sortFields = (a, b) => {
        let nameA = (state.fieldOptions.find(r => r.value === a.alias) || {}).label || a.alias;
        let nameB = (state.fieldOptions.find(r => r.value === b.alias) || {}).label || b.alias;
        return cmp(nameA, nameB);
    };
    const exportProfile = () => {
        let profile = getProfile();
        let config = JSON.stringify(profile);
        let fileName = 'catalog-export-profile.json';
        if(state.profile && state.profile.Name){
            fileName = `catalog-export-profile-${state.profile.Name}.json`;
        }
        let blob = new Blob([config], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, fileName);
    };

    const showImportProfile = () => {
        setState({showProfileImport: true, newProfileName: '', importProfile: null, disableProfileImport: false});
    };

    const onProfileImportLoad = async (reader, name) => {
        let text = reader.result;
        let parsed = JSON.parse(text);
        parsed.Name = name;
        delete parsed.Id;
        delete parsed.Shared;
        await saveProfile(parsed, profileType);
        await onSaveAs(name);
        setState({showProfileImport: false});
    };

    const importProfile = (name, file) => {
        setState({disableProfileImport: true});
        let reader = new FileReader();
        reader.onload = onProfileImportLoad.bind(null, reader, name);
        reader.readAsText(file);
    };

    const onProfileDrop = (files) => {
        if(files && files.length > 0){
            setState({importProfile: files[0]});
        }
    };

    let {isRetailer} = props;
    let filterStr = parseCondition(state.filter, state.attrOptions);
    if(filterStr){
        filterStr = "Export enabled items where " + filterStr + ".";
    } else {
        filterStr = "Export all enabled items.";
    }
    return <Grid container spacing={2}>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>{isRetailer ? 'Profile' : 'Catalog Export'}</Toolbar>
                <CardContent>
                    {state.loaded ? <div style={{display: 'flex', flexDirection: 'column'}}>
                        <div style={{display: 'flex', flex: '1', flexDirection: 'row'}}>
                            <ExtendedSelect innerRef={profileSelectRef} variant='outlined' style={{flex: '1 1 auto', display: 'flex'}} placeholder='Select profile...' value={state.profile.Name || ''} onChange={onProfileSelect} options={state.profiles.map(r => {return {label: r.Name, value: r.Name}})}/>
                            <DownloadButton disabled={!state.profile.Name || !state.spec || !(!state.profile.PartnerCompanyName)} style={{marginRight: '1em', marginLeft: '1em'}} variant='outlined' onClick={showSave}>Save</DownloadButton>
                            <Button style={{marginRight: '1em'}} variant='outlined' onClick={showSaveAs}>Save As</Button>
                            <Button disabled={!state.profile.Name || !state.spec || !(!state.profile.Id)} style={{marginRight: '1em'}} onClick={() => setState({showShare: true})} variant='outlined'>Share</Button>
                            <Button disabled={!state.profile.Name} style={{marginRight: '1em'}} variant='outlined' onClick={() => setState({confirmDelete: true})}>Delete</Button>
                            <Button disabled={!state.profile.Name} style={{marginRight: '1em'}} variant='outlined' onClick={exportProfile}>Save to file</Button>
                            <Button variant='outlined' onClick={showImportProfile}>Load from file</Button>
                        </div>
                        {!isRetailer && <div style={{display: 'flex', flexDirection: 'row', marginTop: '1em'}}>
                            <div style={{display: 'flex', flex: '1', flexDirection: 'column', marginRight: '1em'}}>
                                <ExtendedSelect variant='outlined' placeholder='Select partner...' onChange={onPartnerSwitch} value={state.partner} options={state.partners.map((i) => {return {label: i.CompanyName, value: i.Id};})}/>
                            </div>
                            <DownloadButton variant='contained' color='primary' onClick={sendCatalog} disabled={!state.spec || Object.keys(state.mappings).length === 0}>Export</DownloadButton>
                        </div>}
                        {state.profile.Description && <div style={{textAlign: 'left', marginTop: '1em'}}>
                        {state.profile.Description}
                        </div>}
                    </div> : '' }
                    {!state.loaded && <LinearProgress style={{marginTop: '0.5rem', height: '5px'}}/>}
                    {state.spec || !state.loaded ? '' : <div style={{textAlign: 'left', paddingTop: '0.5em'}}>This partner has no product feed specification.</div>}
                </CardContent>
            </Card>
        </Grid>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>Product Filter</Toolbar>
                <CardContent style={{textAlign: 'left'}}>
                    <Typography style={{marginBottom: '1em'}}>{filterStr}</Typography>
                    <Button variant='outlined' onClick={() => setState({showFilter: true})}>Edit</Button>
                </CardContent>
            </Card>
        </Grid>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>Attribute Mappings</Toolbar>
                {state.missingFields.map(r => <Notifier color="#FFB600" style={{marginTop: '5px', marginRight: '1em', marginLeft: '1em'}} key={r}>Retailer attribute {r} is not mapped.</Notifier>)}
                <div style={{marginTop: '0.5em', textAlign: 'left', marginLeft: '1em'}}>
                    <Button style={{marginRight: '1em'}} variant='outlined' onClick={loadDefaultMappings}>Use default mappings</Button>
                    <Button variant='outlined' onClick={() => setState({showEdit: true, mapping: {}, mappingIndex: null})}>Add Mapping</Button>
                </div>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Retailer Feed Attribute</TableCell>
                            <TableCell>Maps From</TableCell>
                            <TableCell>Condition</TableCell>
                            <TableCell style={{width: '21em'}}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.mappings.map((r, i) => ({r, i})).sort((a, b) => sortFields(a.r, b.r)).map(({r, i}) => <TableRow key={i}>
                            <TableCell>{(state.fieldOptions.find(x => x.value === r.alias) || {}).label || <span>{r.alias} <span style={{color: '#EE3224'}}>This field is not present in the specification.</span></span>}</TableCell>
                            <TableCell>{(state.attrOptions.find(x => x.value === r.field) || {}).label || (isValidExpression(r.field) && r.field) || <span>{r.field} <span style={{ color: '#EE3224' }}>This expression is invalid.</span></span>}</TableCell>
                            <TableCell style={{wordBreak: 'break-word'}}>{parseCondition(r.Condition, state.attrOptions)}</TableCell>
                            <TableCell>
                                <Button variant='outlined' onClick={() => setState({showEdit: true, mapping: r, mappingIndex: i})} style={{marginRight: '1em'}}>Edit</Button>
                                <Button variant='outlined' onClick={() => setState({showEdit: true, mapping: {alias: r.alias}, mappingIndex: null})} style={{marginRight: '1em'}}>Add</Button>
                                <Button variant='outlined' onClick={() => removeMapping(i)}>Remove</Button>
                            </TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </Card>
        </Grid>
        <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>Feed Rules</Toolbar>
                <RuleEditor onChange={rules => setState((prev: any) => ({rules, attrOptions: getAttrOptions(rules, prev.originalAttributes)}))} value={state.rules} disabled={state.disableEdit} fieldOptions={state.originalAttributes}/>
            </Card>
        </Grid>
        <Dialog open={state.showError} onClose={() => {setState({showError: false})}}>
            <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
            <DialogContent><Typography style={{whiteSpace: "pre-wrap"}} dangerouslySetInnerHTML={{__html: state.error}}></Typography></DialogContent>
        </Dialog>
        <Dialog open={state.showExport} onClose={() => {setState({showExport: false})}}>
            <Toolbar className='lbtoolbar'>{'Export Registered'}</Toolbar>
            <DialogContent><Typography style={{whiteSpace: "pre-wrap"}}>{state.exportMessage}</Typography></DialogContent>
            <DialogActions>
                <Button color='primary' component={Link} to='/product-feeds'>Go to product feeds</Button>
            </DialogActions>
        </Dialog>
        <MappingEditor title="Mapping" open={state.showEdit} onClose={() => {setState({showEdit: false})}}
        initialValues={state.mapping} sourceOptions={state.attrOptions} targetOptions={state.fieldOptions}
        targetName='alias' targetLabel='Retailer attribute' sourceName='field' onSubmit={setMapping} disableEdit={state.disableEdit}/>
        <MappingEditor title="Rule" open={state.showRuleEdit} onClose={() => {setState({showRuleEdit: false})}}
        initialValues={state.rule} sourceOptions={state.attrOptions} targetOptions={state.attrOptions} allowNewTarget={true}
        targetName='TargetField' targetLabel='Attribute' sourceName='SourceField' onSubmit={setRuleRef.current} disableEdit={state.disableEdit}/>
        <Dialog open={state.showFilter} onClose={() => {setState({showFilter: false})}} maxWidth='sm' fullWidth>
        <Form onSubmit={setFilter}
        initialValues={{Condition: state.filter}}
        mutators={{...arrayMutators, convertToGroup: convertToGroup}}
        render={({ handleSubmit, pristine, invalid, values, form: {mutators} }) => (
          <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
            <Toolbar className='lbtoolbar'>Edit Filter</Toolbar>
            <DialogContent>
                <RecursiveConditionField name='Condition' value={values.Condition} options={state.attrOptions} mutators={mutators}/>
            </DialogContent>
            <DialogActions>
                <Button color='primary' type='submit' disabled={pristine || invalid || state.disableEdit}>Continue</Button>
            </DialogActions>
            </form>)}/>
        </Dialog>
        <Dialog open={state.showProfileImport} onClose={() => {setState({showProfileImport: false})}} maxWidth='md' fullWidth={true}>
            <Toolbar className='lbtoolbar'>{'Import Profile'}</Toolbar>
            <DialogContent style={{overflow: "hidden"}}>
                <MuiTextField style={{width: '100%', marginBottom: '1em'}} placeholder='New profile name' value={state.newProfileName} onChange={(e) => setState({newProfileName: e.target.value})}/>
                <DropZone accept=".json, .txt" style={{width: '100%', height: '10em'}} onDrop={onProfileDrop}>
                {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                    if (isDragAccept || isDragReject) {
                      return "Drop here to upload this file.";
                    }
                    return state.importProfile ? state.importProfile.name : 'Drop an export profile here or click to choose a file.';
                  }}
                </DropZone>
            </DialogContent>
            <DialogActions><Button color='primary' disabled={!state.newProfileName || state.disableProfileImport || !state.importProfile} onClick={() => importProfile(state.newProfileName, state.importProfile)}>Import</Button></DialogActions>
        </Dialog>
        <DeleteDialog open={state.confirmDelete} onClose={() => setState({confirmDelete: false})} onDelete={onDelete} profileType={profileType} values={getProfile()}/>
        <ShareDialog isRetailer={isRetailer} open={state.showShare} onClose={() => setState({showShare: false})} onShare={shared} profileType={profileType} values={getProfile()}/>
        <SaveAsDialog open={state.showSaveAs} onClose={() => setState({showSaveAs: false})} onSaveAs={onSaveAs} profileType={profileType} values={getProfile()}/>
        <SaveDialog open={state.showSave} onClose={() => setState({showSave: false})} onSave={onSave} profileType={profileType} values={getProfile()}/>
    </Grid>;
}

export default withCatalogEnabled(CatalogExport);
export {profileType, getDefaultMappings};
